import Big, { BigSource } from "big.js";

import { DISCOUNT_TYPE } from "~served/configs/enums";
import { FixDiscount } from "~served/types/db";

import { bigMath } from "../helpers";

import {
	getAppliedFixDiscountByProducts,
	getFixDiscountAmount,
} from "./fix-discount";
import {
	FixDiscountInput,
	FileInputLike,
	FileProductCalculatedValues,
	FileProductInputLike,
	FileProductOptionCalculatedValues,
	FileProductOptionInputLike,
	ObjectLike,
	SubtotalAddonsLike,
	FixDiscountLike,
	AdditionalFeeInput,
	AdditionalFeeLike,
} from "./types";
import { getDiscountAmount } from "./discount";
import {
	getAdditionalFeeAmount,
	getAppliedAdditionalFeeByProducts,
} from "./additional-fee";

const BASE_SUBTOTAL_ADDONS: SubtotalAddonsLike = {
	additionalFee: {
		isDivided: false,
		type: DISCOUNT_TYPE.PERCENTAGE,
		value: 0,
		amount: 0,
		additionalFee: "",
	},
	fixDiscount: {
		isDivided: false,
		type: DISCOUNT_TYPE.PERCENTAGE,
		value: 0,
		amount: 0,
		fixDiscount: "",
	},
	discount: {
		isDivided: false,
		type: DISCOUNT_TYPE.PERCENTAGE,
		value: 0,
		amount: 0,
	},
	vat: { isIncluded: false, percentage: 0, amount: 0 },
	adjustment: { amount: 0 },
};

const applyFixDiscountToProduct = <
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>({
	product,
	discount,
}: {
	product: FileProductInputLike<TFileProduct, TFileProductOption>;
	discount: FixDiscountInput;
}) => {
	const fileProduct = $getProductMetadata(product);
	const { fixDiscountAmount } = getFixDiscountAmount(
		fileProduct.subtotal,
		discount,
	);
	const subtotalAddons = {
		...fileProduct.subtotalAddons,
		fixDiscount: {
			...discount,
			isDivided: false,
			amount: fixDiscountAmount,
		},
	};

	let fileProductOptions = fileProduct.options.map((fileProductOption) =>
		$getOptionMetadata(fileProductOption, fileProduct),
	);
	fileProductOptions = fileProductOptions.map((fileProductOption) => {
		const optionPriceRatioToProduct = bigMath.div(
			fileProductOption.subtotal,
			fileProduct.subtotal,
		);

		return {
			...fileProductOption,
			subtotalAddons: {
				...fileProductOption.subtotalAddons,
				fixDiscount: {
					...discount,
					isDivided: true,
					amount: bigMath.mul(optionPriceRatioToProduct, fixDiscountAmount),
				},
			},
		};
	});

	return {
		...fileProduct,
		subtotalAddons,
		options: fileProductOptions,
	};
};

const applyAdditionalFeeToProduct = <
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>({
	product,
	fee,
}: {
	product: FileProductInputLike<TFileProduct, TFileProductOption>;
	fee: AdditionalFeeInput;
}) => {
	const fileProduct = $getProductMetadata(product);
	const { additionalFeeAmount } = getAdditionalFeeAmount(
		fileProduct.subtotal,
		fee,
	);
	const subtotalAddons = {
		...fileProduct.subtotalAddons,
		additionalFee: {
			...fee,
			isDivided: false,
			amount: additionalFeeAmount,
		},
	};

	let fileProductOptions = fileProduct.options.map((fileProductOption) =>
		$getOptionMetadata(fileProductOption, fileProduct),
	);
	fileProductOptions = fileProductOptions.map((fileProductOption) => {
		const optionPriceRatioToProduct = bigMath.div(
			fileProductOption.subtotal,
			fileProduct.subtotal,
		);

		return {
			...fileProductOption,
			subtotalAddons: {
				...fileProductOption.subtotalAddons,
				additionalFee: {
					...fee,
					isDivided: true,
					amount: bigMath.mul(optionPriceRatioToProduct, additionalFeeAmount),
				},
			},
		};
	});

	return {
		...fileProduct,
		subtotalAddons,
		options: fileProductOptions,
	};
};

const applyAdditionalFeeToFile = <
	TFile extends FileInputLike = FileInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>({
	file,
	additionalFee,
	dividedAmountFromGroup,
}: {
	file: FileInputLike<TFile, TFileProduct, TFileProductOption>;
	additionalFee: AdditionalFeeInput;
	dividedAmountFromGroup?: number;
}) => {
	let fileProducts = file.products.map($deepPopulateProductMetadata);
	const subtotal = fileProducts.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);

	if (additionalFee.type === DISCOUNT_TYPE.PERCENTAGE) {
		fileProducts = fileProducts.map((fileProduct) => {
			const { additionalFeeAmount } = getAdditionalFeeAmount(
				fileProduct.subtotal,
				additionalFee,
			);
			const subtotalAddons = {
				...fileProduct.subtotalAddons,
				additionalFee: {
					...additionalFee,
					isDivided: false,
					amount: additionalFeeAmount,
					additionalFee: additionalFee.additionalFee,
				},
			};

			return { ...fileProduct, subtotalAddons };
		});
	}

	if (additionalFee?.type === DISCOUNT_TYPE.AMOUNT) {
		const { additionalFeeAmount } = dividedAmountFromGroup
			? { additionalFeeAmount: dividedAmountFromGroup }
			: getAdditionalFeeAmount(subtotal, additionalFee);

		let remainingAmountForDivision = additionalFeeAmount;
		fileProducts = fileProducts
			.sort((a, b) => a.subtotal - b.subtotal)
			.map((fileProduct, i) => {
				const isThereAmountToDivide = remainingAmountForDivision > 0;
				const isThisLastProduct = i === fileProducts.length - 1;

				if (isThisLastProduct) {
					const subtotalAddons = {
						...fileProduct.subtotalAddons,
						additionalFee: {
							...additionalFee,
							isDivided: true,
							amount: isThereAmountToDivide ? remainingAmountForDivision : 0,
							additionalFee: additionalFee.additionalFee,
						},
					};

					return { ...fileProduct, subtotalAddons };
				}

				const productPriceRatioToFile = bigMath.div(
					fileProduct.subtotal,
					subtotal,
				);
				let dividedDiscountAmountForProduct = isThereAmountToDivide
					? bigMath.mul(additionalFeeAmount, productPriceRatioToFile)
					: 0;
				dividedDiscountAmountForProduct =
					dividedDiscountAmountForProduct > (fileProduct.subtotal || 0)
						? fileProduct.subtotal || 0
						: dividedDiscountAmountForProduct;

				const subtotalAddons = {
					...fileProduct.subtotalAddons,
					additionalFee: {
						...additionalFee,
						isDivided: true,
						amount: dividedDiscountAmountForProduct,
						additionalFee: additionalFee.additionalFee,
					},
				};

				remainingAmountForDivision = bigMath.sub(
					remainingAmountForDivision,
					dividedDiscountAmountForProduct,
				);

				return { ...fileProduct, subtotalAddons };
			});
	}

	// options only stats
	fileProducts = fileProducts.map((fileProduct) => ({
		...fileProduct,
		options: fileProduct.options.map((fileProductOption) => {
			const optionPriceRatioToProduct = bigMath.div(
				fileProductOption.subtotal,
				fileProduct.subtotal,
			);

			return {
				...fileProductOption,
				subtotalAddons: {
					...fileProductOption.subtotalAddons,
					additionalFee: {
						...additionalFee,
						isDivided: true,
						amount: bigMath.mul(
							optionPriceRatioToProduct,
							fileProduct.subtotalAddons.additionalFee.amount,
						),
						additionalFee:
							fileProduct.subtotalAddons.additionalFee.additionalFee,
					},
				},
			};
		}),
	}));

	const newAdditionalFeeAmount = fileProducts.reduce(
		(pre, cur) =>
			bigMath.add(pre, cur.subtotalAddons.additionalFee.amount || 0),
		0,
	);

	return calculateFile({
		...file,
		products: fileProducts,
		subtotal,
		additionalFeeAmount: newAdditionalFeeAmount,
	}).$file;
};

const applyFixDiscountToFile = <
	TFile extends FileInputLike = FileInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>({
	file,
	fixDiscount,
	dividedAmountFromGroup,
}: {
	file: FileInputLike<TFile, TFileProduct, TFileProductOption>;
	fixDiscount: FixDiscountInput;
	dividedAmountFromGroup?: number;
}) => {
	let fileProducts = file.products.map($deepPopulateProductMetadata);
	const subtotal = fileProducts.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);

	if (fixDiscount.type === DISCOUNT_TYPE.PERCENTAGE) {
		fileProducts = fileProducts.map((fileProduct) => {
			const { fixDiscountAmount } = getFixDiscountAmount(
				fileProduct.subtotal,
				fixDiscount,
			);
			const subtotalAddons = {
				...fileProduct.subtotalAddons,
				fixDiscount: {
					...fixDiscount,
					isDivided: false,
					amount: fixDiscountAmount,
					fixDiscount: fixDiscount.fixDiscount,
				},
			};

			return { ...fileProduct, subtotalAddons };
		});
	}

	if (fixDiscount?.type === DISCOUNT_TYPE.AMOUNT) {
		const { fixDiscountAmount } = dividedAmountFromGroup
			? { fixDiscountAmount: dividedAmountFromGroup }
			: getFixDiscountAmount(subtotal, fixDiscount);

		let remainingAmountForDivision = fixDiscountAmount;
		fileProducts = fileProducts
			.sort((a, b) => a.subtotal - b.subtotal)
			.map((fileProduct, i) => {
				const isThereAmountToDivide = remainingAmountForDivision > 0;
				const isThisLastProduct = i === fileProducts.length - 1;

				if (isThisLastProduct) {
					const subtotalAddons = {
						...fileProduct.subtotalAddons,
						fixDiscount: {
							...fixDiscount,
							isDivided: true,
							amount: isThereAmountToDivide ? remainingAmountForDivision : 0,
							fixDiscount: fixDiscount.fixDiscount,
						},
					};

					return { ...fileProduct, subtotalAddons };
				}

				const productPriceRatioToFile = bigMath.div(
					fileProduct.subtotal,
					subtotal,
				);
				let dividedDiscountAmountForProduct = isThereAmountToDivide
					? bigMath.mul(fixDiscountAmount, productPriceRatioToFile)
					: 0;
				dividedDiscountAmountForProduct =
					dividedDiscountAmountForProduct > (fileProduct.subtotal || 0)
						? fileProduct.subtotal || 0
						: dividedDiscountAmountForProduct;

				const subtotalAddons = {
					...fileProduct.subtotalAddons,
					fixDiscount: {
						...fixDiscount,
						isDivided: true,
						amount: dividedDiscountAmountForProduct,
						fixDiscount: fixDiscount.fixDiscount,
					},
				};

				remainingAmountForDivision = bigMath.sub(
					remainingAmountForDivision,
					dividedDiscountAmountForProduct,
				);

				return { ...fileProduct, subtotalAddons };
			});
	}

	// options only stats
	fileProducts = fileProducts.map((fileProduct) => ({
		...fileProduct,
		options: fileProduct.options.map((fileProductOption) => {
			const optionPriceRatioToProduct = bigMath.div(
				fileProductOption.subtotal,
				fileProduct.subtotal,
			);

			return {
				...fileProductOption,
				subtotalAddons: {
					...fileProductOption.subtotalAddons,
					fixDiscount: {
						...fixDiscount,
						isDivided: true,
						amount: bigMath.mul(
							optionPriceRatioToProduct,
							fileProduct.subtotalAddons.fixDiscount.amount,
						),
						fixDiscount: fileProduct.subtotalAddons.fixDiscount.fixDiscount,
					},
				},
			};
		}),
	}));

	const newFixDiscountAmount = fileProducts.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotalAddons.fixDiscount.amount || 0),
		0,
	);

	return calculateFile({
		...file,
		products: fileProducts,
		subtotal,
		fixDiscountAmount: newFixDiscountAmount,
	}).$file;
};

const applyNonOrderAdditionalFeeToFile = <
	TFile extends FileInputLike = FileInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>({
	file,
	additionalFee,
}: {
	file: FileInputLike<TFile, TFileProduct, TFileProductOption>;
	additionalFee: AdditionalFeeLike;
}) => {
	let fileProducts = file.products
		.map($deepPopulateProductMetadata)
		.map((f) => ({
			...f,
			options: f.options.map((o) => $getOptionMetadata(o, f)),
		}));
	const subtotal = fileProducts.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);

	const { eligibleProducts } = getAppliedAdditionalFeeByProducts(
		fileProducts,
		additionalFee,
	);

	fileProducts = fileProducts.map((f) => ({
		...f,
		subtotalAddons: {
			...f.subtotalAddons,
			additionalFee: {
				value: eligibleProducts[f._id.toString()]
					? eligibleProducts[f._id.toString()].value
					: 0,
				amount: eligibleProducts[f._id.toString()]
					? eligibleProducts[f._id.toString()].value
					: 0,
				type: eligibleProducts[f._id.toString()]
					? eligibleProducts[f._id.toString()].type
					: "PERCENTAGE",
				isDivided: true,
				additionalFee: additionalFee._id,
			},
		},
	}));

	// options only stats
	fileProducts = fileProducts.map((f) => ({
		...f,
		options: f.options.map((o) => {
			const itemEligibleAmount = eligibleProducts[f._id.toString()];
			const optionPriceRatioToItem = bigMath.div(o.subtotal, f.subtotal);
			const optionEligibleAmount = bigMath.mul(
				optionPriceRatioToItem,
				itemEligibleAmount ? itemEligibleAmount.value : 0,
			);

			return {
				...o,
				subtotalAddons: {
					...o.subtotalAddons,
					offer: {
						value: optionEligibleAmount,
						amount: optionEligibleAmount,
						type: itemEligibleAmount ? itemEligibleAmount.type : "PERCENTAGE",
						isDivided: true,
					},
				},
			};
		}),
	}));

	const newAdditionalFeeAmount = fileProducts.reduce(
		(pre, cur) =>
			bigMath.add(pre, cur.subtotalAddons.additionalFee.amount || 0),
		0,
	);

	return calculateFile({
		...file,
		products: fileProducts,
		subtotal,
		additionalFeeAmount: newAdditionalFeeAmount,
	}).$file;
};

const applyNonOrderFixDiscountToFile = <
	TFile extends FileInputLike = FileInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>({
	file,
	fixDiscount,
}: {
	file: FileInputLike<TFile, TFileProduct, TFileProductOption>;
	fixDiscount: FixDiscountLike;
}) => {
	let fileProducts = file.products
		.map($deepPopulateProductMetadata)
		.map((f) => ({
			...f,
			options: f.options.map((o) => $getOptionMetadata(o, f)),
		}));
	const subtotal = fileProducts.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);

	const { eligibleProducts } = getAppliedFixDiscountByProducts(
		fileProducts,
		fixDiscount,
	);

	fileProducts = fileProducts.map((f) => ({
		...f,
		subtotalAddons: {
			...f.subtotalAddons,
			fixDiscount: {
				value: eligibleProducts[f._id.toString()]
					? eligibleProducts[f._id.toString()].value
					: 0,
				amount: eligibleProducts[f._id.toString()]
					? eligibleProducts[f._id.toString()].value
					: 0,
				type: eligibleProducts[f._id.toString()]
					? eligibleProducts[f._id.toString()].type
					: "PERCENTAGE",
				isDivided: true,
				fixDiscount: fixDiscount._id,
			},
		},
	}));

	// options only stats
	fileProducts = fileProducts.map((f) => ({
		...f,
		options: f.options.map((o) => {
			const itemEligibleAmount = eligibleProducts[f._id.toString()];
			const optionPriceRatioToItem = bigMath.div(o.subtotal, f.subtotal);
			const optionEligibleAmount = bigMath.mul(
				optionPriceRatioToItem,
				itemEligibleAmount ? itemEligibleAmount.value : 0,
			);

			return {
				...o,
				subtotalAddons: {
					...o.subtotalAddons,
					offer: {
						value: optionEligibleAmount,
						amount: optionEligibleAmount,
						type: itemEligibleAmount ? itemEligibleAmount.type : "PERCENTAGE",
						isDivided: true,
					},
				},
			};
		}),
	}));

	const newFixDiscountAmount = fileProducts.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotalAddons.fixDiscount.amount || 0),
		0,
	);

	return calculateFile({
		...file,
		products: fileProducts,
		subtotal,
		fixDiscountAmount: newFixDiscountAmount,
	}).$file;
};

const applyAdditionalFeeToFileGroup = <
	TFile extends FileInputLike = FileInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>({
	files: allFiles,
	additionalFee,
	skipPaidCheck = false,
}: {
	files: FileInputLike<TFile, TFileProduct, TFileProductOption>[];
	additionalFee: AdditionalFeeInput;
	skipPaidCheck?: boolean;
}) => {
	const unpaidFiles = allFiles.filter((o) =>
		skipPaidCheck ? true : $isFileUnpaid(o),
	);
	let updatedFiles: FileInputLike[] = [];

	if (additionalFee.type === DISCOUNT_TYPE.PERCENTAGE) {
		updatedFiles = unpaidFiles.map((file) =>
			applyAdditionalFeeToFile({ file, additionalFee }),
		);
	}

	if (additionalFee.type === DISCOUNT_TYPE.AMOUNT) {
		const fileGroupSubTotal = unpaidFiles.reduce(
			(pre, cur) => bigMath.add(pre, cur.subtotal),
			0,
		);
		const { additionalFeeAmount } = getAdditionalFeeAmount(
			fileGroupSubTotal,
			additionalFee,
		);

		let remainingAmountForDivision = additionalFeeAmount;
		updatedFiles = unpaidFiles
			.sort((a, b) => a.subtotal - b.subtotal)
			.map((file, i) => {
				const isThereAmountToDivide = remainingAmountForDivision > 0;
				const isThisLastProduct = i === unpaidFiles.length - 1;

				if (isThisLastProduct) {
					return applyAdditionalFeeToFile({
						file,
						additionalFee,
						dividedAmountFromGroup: isThereAmountToDivide
							? remainingAmountForDivision
							: 0,
					});
				}

				const filePriceRatioToGroup = bigMath.div(
					file.subtotal,
					fileGroupSubTotal,
				);
				const dividedDiscountAmountForFile = isThereAmountToDivide
					? bigMath.mul(additionalFeeAmount, filePriceRatioToGroup)
					: 0;

				remainingAmountForDivision = bigMath.sub(
					remainingAmountForDivision,
					dividedDiscountAmountForFile,
				);

				return applyAdditionalFeeToFile({
					file,
					additionalFee,
					dividedAmountFromGroup: dividedDiscountAmountForFile,
				});
			});
	}

	return updatedFiles;
};

const applyFixDiscountToFileGroup = <
	TFile extends FileInputLike = FileInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>({
	files: allFiles,
	fixDiscount,
	skipPaidCheck = false,
}: {
	files: FileInputLike<TFile, TFileProduct, TFileProductOption>[];
	fixDiscount: FixDiscountInput;
	skipPaidCheck?: boolean;
}) => {
	const unpaidFiles = allFiles.filter((o) =>
		skipPaidCheck ? true : $isFileUnpaid(o),
	);
	let updatedFiles: FileInputLike[] = [];

	if (fixDiscount.type === DISCOUNT_TYPE.PERCENTAGE) {
		updatedFiles = unpaidFiles.map((file) =>
			applyFixDiscountToFile({ file, fixDiscount }),
		);
	}

	if (fixDiscount.type === DISCOUNT_TYPE.AMOUNT) {
		const fileGroupSubTotal = unpaidFiles.reduce(
			(pre, cur) => bigMath.add(pre, cur.subtotal),
			0,
		);
		const { fixDiscountAmount } = getFixDiscountAmount(
			fileGroupSubTotal,
			fixDiscount,
		);

		let remainingAmountForDivision = fixDiscountAmount;
		updatedFiles = unpaidFiles
			.sort((a, b) => a.subtotal - b.subtotal)
			.map((file, i) => {
				const isThereAmountToDivide = remainingAmountForDivision > 0;
				const isThisLastProduct = i === unpaidFiles.length - 1;

				if (isThisLastProduct) {
					return applyFixDiscountToFile({
						file,
						fixDiscount,
						dividedAmountFromGroup: isThereAmountToDivide
							? remainingAmountForDivision
							: 0,
					});
				}

				const filePriceRatioToGroup = bigMath.div(
					file.subtotal,
					fileGroupSubTotal,
				);
				const dividedDiscountAmountForFile = isThereAmountToDivide
					? bigMath.mul(fixDiscountAmount, filePriceRatioToGroup)
					: 0;

				remainingAmountForDivision = bigMath.sub(
					remainingAmountForDivision,
					dividedDiscountAmountForFile,
				);

				return applyFixDiscountToFile({
					file,
					fixDiscount,
					dividedAmountFromGroup: dividedDiscountAmountForFile,
				});
			});
	}

	return updatedFiles;
};

const applyVatToFile = <
	TFile extends FileInputLike = FileInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>({
	file,
	vat,
}: {
	file: FileInputLike<TFile, TFileProduct, TFileProductOption>;
	vat: number;
}) => {
	let fileProducts = file.products.map($deepPopulateProductMetadata);
	const subtotal = fileProducts.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);

	fileProducts = fileProducts.map((fileProduct) => {
		const isIncluded =
			!!fileProduct.originalPriceAddons.vat.percentage &&
			!!fileProduct.originalPriceAddons.vat.amount;
		const vatPercentage = isIncluded
			? fileProduct.originalPriceAddons.vat.percentage
			: fileProduct.noVat
				? 0
				: vat;

		const grossSales = bigMath.sub(
			fileProduct.subtotal,
			fileProduct.subtotalAddons.fixDiscount.amount,
			fileProduct.subtotalAddons.discount.amount,
		);
		const addedVatScRate = vatPercentage;
		const vatAmount = new Big(
			new Big(
				new Big(grossSales).sub(
					new Big(grossSales).div(new Big(1).add(addedVatScRate)),
				),
			).mul(vatPercentage),
		)
			.div(addedVatScRate || 1)
			.round(2)
			.toNumber();

		const subtotalAddons = {
			...fileProduct.subtotalAddons,
			vat: {
				isIncluded: isIncluded,
				percentage: vatPercentage,
				amount: vatAmount,
			},
		};

		return { ...fileProduct, subtotalAddons };
	});

	// options only stats
	fileProducts = fileProducts.map((fileProduct) => ({
		...fileProduct,
		options: fileProduct.options.map((fileProductOption) => {
			const isIncluded =
				!!fileProductOption.originalPriceAddons.vat.percentage &&
				!!fileProductOption.originalPriceAddons.vat.amount;
			const vatPercentage = isIncluded
				? fileProductOption.originalPriceAddons.vat.percentage
				: fileProduct.noVat
					? 0
					: vat;

			const grossSales = bigMath.sub(
				fileProductOption.subtotal,
				fileProductOption.subtotalAddons.fixDiscount.amount,
				fileProductOption.subtotalAddons.discount.amount,
			);
			const addedVatScRate = vatPercentage;
			const vatAmount = new Big(
				new Big(
					new Big(grossSales).sub(
						new Big(grossSales).div(new Big(1).add(addedVatScRate)),
					),
				).mul(vatPercentage),
			)
				.div(addedVatScRate || 1)
				.round(2)
				.toNumber();

			return {
				...fileProductOption,
				subtotalAddons: {
					...fileProductOption.subtotalAddons,
					vat: {
						isIncluded: isIncluded,
						percentage: vatPercentage,
						amount: vatAmount,
					},
				},
			};
		}),
	}));

	const newVatAmount = fileProducts.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotalAddons.vat.amount),
		0,
	);

	return calculateFile({
		...file,
		products: fileProducts,
		subtotal,
		vatAmount: newVatAmount,
	}).$file;
};

const applyVatToFileGroup = <
	TFile extends FileInputLike = FileInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>({
	files,
	vat,
	skipPaidCheck = false,
}: {
	files: FileInputLike<TFile, TFileProduct, TFileProductOption>[];
	vat: number;
	skipPaidCheck?: boolean;
}) => {
	return files
		.filter((o) => (skipPaidCheck ? true : $isFileUnpaid(o)))
		.map((file) => applyVatToFile({ file, vat }));
};

const calculateFile = <
	TFile extends FileInputLike = FileInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>(
	file: FileInputLike<TFile, TFileProduct, TFileProductOption>,
) => {
	let fileProducts = file.products.map($deepPopulateProductMetadata);
	fileProducts = fileProducts.map((fileProduct) => {
		const subtotalAddons = $getSubtotalAddons({
			product: fileProduct,
			isCancelled: file.isCancelled,
		});
		const { netAmount, grossAmount } = $getProductWithNetAndGrossAmount({
			...fileProduct,
			subtotalAddons,
		});
		const cancelledAmount = 0;

		return {
			...fileProduct,
			subtotalAddons,
			netAmount,
			grossAmount,
			cancelledAmount,
		};
	});

	fileProducts = fileProducts.map((fileProduct) => ({
		...fileProduct,
		options: fileProduct.options.map((fileProductOption) => {
			const subtotalAddons = $getOptionSubtotalAddons({
				option: fileProductOption,
				product: fileProduct,
				isCancelled: file.isCancelled,
			});
			const { netAmount, grossAmount } = $getOptionWithNetAndGrossAmount(
				{
					...fileProductOption,
					subtotalAddons,
				},
				fileProduct,
			);
			const cancelledAmount = 0;

			return {
				...fileProductOption,
				subtotalAddons,
				netAmount,
				grossAmount,
				cancelledAmount,
			};
		}),
	}));

	let cancelledProducts = file.cancelledProducts.map(
		$deepPopulateProductMetadata,
	);
	cancelledProducts = cancelledProducts.map((fileProduct) => {
		const subtotal = 0;
		const subtotalAddons = $getSubtotalAddons({
			product: { ...fileProduct, subtotal },
			isCancelled: true,
		});
		const netAmount = 0;
		const grossAmount = 0;
		const cancelledAmount = bigMath.sub(
			$getProductSubtotal({ ...fileProduct, subtotal, netAmount, grossAmount }),
			$getProductTotalRawPriceAddons({
				...fileProduct,
				subtotal,
				netAmount,
				grossAmount,
			}),
		); // to exclude any buried in amount so this is only `net` amount

		return {
			...fileProduct,
			subtotal,
			subtotalAddons,
			netAmount,
			grossAmount,
			cancelledAmount,
		};
	});
	cancelledProducts = cancelledProducts.map((fileProduct) => ({
		...fileProduct,
		options: fileProduct.options.map((fileProductOption) => {
			const subtotal = 0;
			const subtotalAddons = $getOptionSubtotalAddons({
				option: { ...fileProductOption, subtotal },
				product: fileProduct,
				isCancelled: true,
			});
			const netAmount = 0;
			const grossAmount = 0;
			const cancelledAmount = bigMath.sub(
				$getOptionSubtotal(
					{ ...fileProductOption, subtotal, netAmount, grossAmount },
					fileProduct,
				),
				$getOptionTotalRawPriceAddons(
					{
						...fileProductOption,
						subtotal,
						netAmount,
						grossAmount,
					},
					fileProduct,
				),
			); // to exclude any buried in amount so this is only `net` amount

			return {
				...fileProductOption,
				subtotal,
				subtotalAddons,
				netAmount,
				grossAmount,
				cancelledAmount,
			};
		}),
	}));

	const subtotal = fileProducts.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);
	const additionalFeeAmount = fileProducts.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotalAddons.additionalFee.amount),
		0,
	);
	const fixDiscountAmount = fileProducts.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotalAddons.fixDiscount.amount),
		0,
	);
	const discountAmount = fileProducts.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotalAddons.discount.amount),
		0,
	);
	const netAmount = fileProducts.reduce(
		(pre, cur) => bigMath.add(pre, cur.netAmount),
		0,
	);
	const vatAmount = fileProducts.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotalAddons.vat.amount),
		0,
	);
	const adjustmentAmount = fileProducts.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotalAddons.adjustment.amount),
		0,
	);
	const grossAmount = fileProducts.reduce(
		(pre, cur) => bigMath.add(pre, cur.grossAmount),
		0,
	);
	let paymentAmount = 0;
	for (const payment of file.receipt?.payments ?? []) {
		for (const p of payment.payments) {
			paymentAmount = bigMath.add(paymentAmount, p.amount);
		}
	}
	const unpaidAmount = bigMath.sub(grossAmount, paymentAmount);
	const cancelledAmount = cancelledProducts.reduce(
		(pre, cur) => bigMath.add(pre, cur.cancelledAmount),
		0,
	);

	const $file = {
		...file,
		products: fileProducts,
		cancelledProducts,
	};

	return {
		$file,
		$isAllProductsWithVat:
			!!fileProducts.length &&
			fileProducts.every((product) => !!product.subtotalAddons.vat.amount),
		$isSomeProductsWithVat:
			!!fileProducts.length &&
			fileProducts.some((product) => !!product.subtotalAddons.vat.amount),
		$isAllProductsWithFixDiscount:
			!!fileProducts.length &&
			fileProducts.every(
				(product) =>
					!!product.subtotalAddons.fixDiscount.type &&
					!!product.subtotalAddons.fixDiscount.value,
			) &&
			fileProducts.every(
				(product) =>
					product.subtotalAddons.fixDiscount.type ===
						fileProducts[0].subtotalAddons.fixDiscount.type &&
					product.subtotalAddons.fixDiscount.value ===
						fileProducts[0].subtotalAddons.fixDiscount.value,
			),
		$isAllProductsWithAdditionalFee:
			!!fileProducts.length &&
			fileProducts.every(
				(product) =>
					!!product.subtotalAddons.additionalFee.type &&
					!!product.subtotalAddons.additionalFee.value,
			) &&
			fileProducts.every(
				(product) =>
					product.subtotalAddons.additionalFee.type ===
						fileProducts[0].subtotalAddons.additionalFee.type &&
					product.subtotalAddons.additionalFee.value ===
						fileProducts[0].subtotalAddons.additionalFee.value,
			),
		products: fileProducts,
		cancelledProducts,
		subtotal,
		additionalFeeAmount,
		fixDiscountAmount,
		discountAmount,
		netAmount,
		vatAmount,
		adjustmentAmount,
		grossAmount,
		paymentAmount,
		unpaidAmount,
		cancelledAmount,
	};
};

const calculateFileGroup = <
	TFile extends FileInputLike = FileInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>(
	files: FileInputLike<TFile, TFileProduct, TFileProductOption>[],
) => {
	return files.reduce(
		(pre, cur) => {
			const isFileUnpaid = $isFileUnpaid(cur);
			const isFilePaid = !isFileUnpaid;

			return {
				all: $addFileToFileGroupCalculation(pre.all, cur),
				paid: isFilePaid
					? $addFileToFileGroupCalculation(pre.paid, cur)
					: pre.paid,
				unpaid: isFileUnpaid
					? $addFileToFileGroupCalculation(pre.unpaid, cur)
					: pre.unpaid,
			};
		},
		{
			all: baseFileGroupValue,
			paid: baseFileGroupValue,
			unpaid: baseFileGroupValue,
		},
	);
};

const calculateReportFileGroup = <
	TFile extends FileInputLike = FileInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>(
	files: FileInputLike<TFile, TFileProduct, TFileProductOption>[],
) => {
	const allSummary = files.reduce((pre, cur) => {
		return $addFileToReportCalculation(pre, cur);
	}, baseReportValue);
	const invoices = files.filter(
		(f) => f.receipt && !f.receipt.isFullyPaid === true,
	);
	const receipts = files.filter((f) => f.receipt?.isFullyPaid === true);

	const invoiceSummary = invoices.reduce((pre, cur) => {
		return { ...$addFileToReportCalculation(pre, cur) };
	}, baseReportValue);

	const receiptSummary = receipts.reduce((pre, cur) => {
		return { ...$addFileToReportCalculation(pre, cur) };
	}, baseReportValue);

	return {
		all: {
			...allSummary,
			orderCount: files.length,
			invoiceCount: invoices.length,
			receiptCount: receipts.length,
			averageOrderAmount: bigMath
				.div(allSummary.grossAmount, files.length)
				.toFixed(2),
			averageInvoiceAmount: bigMath
				.div(invoiceSummary.grossAmount, invoices.length)
				.toFixed(2),
			averageReceiptAmount: bigMath
				.div(receiptSummary.grossAmount, receipts.length)
				.toFixed(2),
		},
		invoice: invoiceSummary,
		receipts: receiptSummary,
	};
};

const $getProductWithNetAndGrossAmount = <
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>(
	product: FileProductInputLike<TFileProduct, TFileProductOption>,
) => {
	product = {
		...product,
		subtotalAddons: {
			...BASE_SUBTOTAL_ADDONS,
			...product.subtotalAddons,
		},
	};

	const isVatIncluded =
		!!product.originalPriceAddons.vat.percentage &&
		!!product.originalPriceAddons.vat.amount;

	let [grossAmount, netAmount] = [0, 0];

	if (isVatIncluded) {
		grossAmount = bigMath.add(
			bigMath.sub(
				product.subtotal,
				product.subtotalAddons.fixDiscount.amount,
				product.subtotalAddons.discount.amount,
			),
			product.subtotalAddons.adjustment.amount,
			product.subtotalAddons.additionalFee.amount,
		);
		netAmount = bigMath.sub(grossAmount, product.subtotalAddons.vat.amount);
	} else {
		netAmount = bigMath.sub(
			product.subtotal,
			$getProductTotalRawPriceAddons(product),
			product.subtotalAddons.fixDiscount.amount,
			product.subtotalAddons.discount.amount,
		);

		grossAmount = bigMath.add(
			netAmount,
			product.subtotalAddons.vat.amount,
			product.subtotalAddons.adjustment.amount,
			product.subtotalAddons.additionalFee.amount,
		);
	}

	return { ...product, netAmount, grossAmount };
};

const $getOptionWithNetAndGrossAmount = <
	TFileOption extends FileProductOptionInputLike = FileProductOptionInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
>(
	option: FileProductOptionInputLike<TFileOption>,
	product: FileProductInputLike<TFileProduct, TFileOption>,
) => {
	option = {
		...option,
		subtotalAddons: {
			...BASE_SUBTOTAL_ADDONS,
			...option.subtotalAddons,
		},
	};

	const isVatIncluded =
		!!option.originalPriceAddons.vat.percentage &&
		!!option.originalPriceAddons.vat.amount;

	let [grossAmount, netAmount] = [0, 0];

	if (isVatIncluded) {
		grossAmount = bigMath.add(
			bigMath.sub(
				option.subtotal,
				option.subtotalAddons.fixDiscount.amount,
				option.subtotalAddons.discount.amount,
			),
			option.subtotalAddons.adjustment.amount,
			option.subtotalAddons.additionalFee.amount,
		);
		netAmount = bigMath.sub(grossAmount, option.subtotalAddons.vat.amount);
	} else {
		netAmount = bigMath.sub(
			option.subtotal,
			$getOptionTotalRawPriceAddons(option, product),
			option.subtotalAddons.fixDiscount.amount,
			option.subtotalAddons.discount.amount,
		);

		grossAmount = bigMath.add(
			netAmount,
			option.subtotalAddons.vat.amount,
			option.subtotalAddons.adjustment.amount,
			option.subtotalAddons.additionalFee.amount,
		);
	}

	return { ...option, netAmount, grossAmount };
};

const getFixDiscountsUsedFromFiles = <
	TFile extends FileInputLike = FileInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>(
	files: FileInputLike<TFile, TFileProduct, TFileProductOption>[],
) => {
	const fixDiscountsUsedDictionary: Record<
		string,
		{ fixDiscount: ObjectLike; amount: number }
	> = {};

	for (const i of files.map((o) => o.products).flat()) {
		if (
			i.subtotalAddons?.fixDiscount.amount &&
			i.subtotalAddons.fixDiscount.metadata
		) {
			const key = (i.subtotalAddons.fixDiscount.metadata as FixDiscount)._id;

			if (fixDiscountsUsedDictionary[key]) {
				fixDiscountsUsedDictionary[key].amount = bigMath.add(
					fixDiscountsUsedDictionary[key].amount,
					i.subtotalAddons?.fixDiscount?.amount,
				);
			} else {
				fixDiscountsUsedDictionary[key] = {
					fixDiscount: i.subtotalAddons.fixDiscount.metadata,
					amount: i.subtotalAddons.fixDiscount.amount,
				};
			}
		}
	}

	return Object.values(fixDiscountsUsedDictionary);
};

export {
	applyAdditionalFeeToFileGroup,
	applyAdditionalFeeToFile,
	applyNonOrderAdditionalFeeToFile,
	applyAdditionalFeeToProduct,
	applyFixDiscountToFileGroup,
	applyFixDiscountToFile,
	applyNonOrderFixDiscountToFile,
	applyFixDiscountToProduct,
	applyVatToFile,
	applyVatToFileGroup,
	calculateFile,
	calculateFileGroup,
	calculateReportFileGroup,
	getFixDiscountsUsedFromFiles,
	$getProductWithNetAndGrossAmount as getProductWithNetAndGrossAmount,
	$getSubtotalAddons as getSubtotalAddons,
};

const $deepPopulateProductMetadata = <
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>(
	fileProduct,
): FileProductInputLike<
	TFileProduct & FileProductCalculatedValues,
	TFileProductOption & FileProductOptionCalculatedValues
> => {
	const updatedProduct = $getProductMetadata(fileProduct);
	return {
		...updatedProduct,
		options: updatedProduct.options.map((fileProductOption) =>
			$getOptionMetadata(fileProductOption, updatedProduct),
		),
	};
};

const $getOptionServingQuantity = <
	TFileOption extends FileProductOptionInputLike = FileProductOptionInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
>(
	option: FileProductOptionInputLike<TFileOption>,
	product: FileProductInputLike<TFileProduct, TFileOption>,
) => bigMath.mul(option.quantity, product.quantity);

const $getOptionSubtotal = <
	TFileOption extends FileProductOptionInputLike = FileProductOptionInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
>(
	option: FileProductOptionInputLike<TFileOption>,
	product: FileProductInputLike<TFileProduct, TFileOption>,
) =>
	bigMath.mul(option.listedPrice, $getOptionServingQuantity(option, product));

const $getOptionMetadata = <
	TFileOption extends FileProductOptionInputLike = FileProductOptionInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
>(
	option: FileProductOptionInputLike<TFileOption>,
	product: FileProductInputLike<TFileProduct, TFileOption>,
): FileProductOptionInputLike<
	TFileOption & FileProductOptionCalculatedValues
> => ({
	netAmount: 0,
	grossAmount: 0,
	cancelledAmount: 0,
	...option,
	unitPrice: $getFileProductOptionUnitPrice(option),
	subtotal: $getOptionSubtotal(option, product),
	subtotalAddons: {
		...BASE_SUBTOTAL_ADDONS,
		...option.subtotalAddons,
	},
});

const $getProductMetadata = <
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>(
	product: FileProductInputLike<TFileProduct, TFileProductOption>,
): FileProductInputLike<
	TFileProduct & FileProductCalculatedValues,
	TFileProductOption
> => ({
	netAmount: 0,
	grossAmount: 0,
	cancelledAmount: 0,
	...product,
	unitPrice: $getProductUnitPrice(product),
	subtotal: $getProductSubtotal(product),
	subtotalAddons: {
		...BASE_SUBTOTAL_ADDONS,
		...product.subtotalAddons,
	},
});

const $isFileUnpaid = <
	TFile extends FileInputLike = FileInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>(
	file: FileInputLike<TFile, TFileProduct, TFileProductOption>,
) => !file.receipt && !file.isCancelled;

const $getSubtotalAddons = <
	TFileProduct extends
		FileProductInputLike<FileProductCalculatedValues> = FileProductInputLike<FileProductCalculatedValues>,
	TFileProductOption extends
		FileProductOptionInputLike<FileProductOptionCalculatedValues> = FileProductOptionInputLike<FileProductOptionCalculatedValues>,
>({
	product,
	isCancelled,
}: {
	product: FileProductInputLike<TFileProduct, TFileProductOption>;
	isCancelled: boolean;
}) => {
	if (isCancelled) return BASE_SUBTOTAL_ADDONS;

	product = {
		...product,
		subtotalAddons: {
			...BASE_SUBTOTAL_ADDONS,
			...product.subtotalAddons,
		},
	};
	const { originalPriceAddons, subtotal, subtotalAddons } = product;

	const newDiscountIsDivided = subtotalAddons.discount.isDivided;
	const newDiscountType = subtotalAddons.discount.type;
	const newDiscountValue = subtotalAddons.discount.value;
	const newDiscountAmount =
		newDiscountType === DISCOUNT_TYPE.PERCENTAGE || !newDiscountIsDivided
			? getDiscountAmount(subtotal, {
					type: newDiscountType,
					value: newDiscountValue,
				}).discountAmount
			: subtotalAddons.discount.amount;

	const newAdditionalFeeIsDivided = subtotalAddons.additionalFee.isDivided;
	const newAdditionalFeeType = subtotalAddons.additionalFee.type;
	const newAdditionalFeeValue = subtotalAddons.additionalFee.value;
	const newAdditionalFeeAmount =
		newAdditionalFeeType === DISCOUNT_TYPE.PERCENTAGE ||
		!newAdditionalFeeIsDivided
			? getAdditionalFeeAmount(subtotal, {
					type: newAdditionalFeeType,
					value: newAdditionalFeeValue,
				}).additionalFeeAmount
			: subtotalAddons.additionalFee.amount;

	const newFixDiscountIsDivided = subtotalAddons.fixDiscount.isDivided;
	const newFixDiscountType = subtotalAddons.fixDiscount.type;
	const newFixDiscountValue = subtotalAddons.fixDiscount.value;
	const newFixDiscountAmount =
		newFixDiscountType === DISCOUNT_TYPE.PERCENTAGE || !newFixDiscountIsDivided
			? getFixDiscountAmount(subtotal, {
					type: newFixDiscountType,
					value: newFixDiscountValue,
				}).fixDiscountAmount
			: subtotalAddons.fixDiscount.amount;

	const newVatIsIncluded =
		!!originalPriceAddons.vat.percentage && !!originalPriceAddons.vat.amount;
	const newVatPercentage = newVatIsIncluded
		? originalPriceAddons.vat.percentage
		: subtotalAddons.vat.percentage;

	let [newVatAmount] = [0];
	const grossSales = bigMath.add(
		bigMath.sub(
			subtotal,
			subtotalAddons.fixDiscount.amount,
			subtotalAddons.discount.amount,
		),
		subtotalAddons.additionalFee.amount,
	);
	const addedVatScRate = bigMath.add(newVatPercentage);
	newVatAmount = newVatIsIncluded
		? new Big(
				new Big(
					new Big(grossSales).sub(
						new Big(grossSales).div(new Big(1).add(addedVatScRate)),
					),
				).mul(newVatPercentage),
			)
				.div(addedVatScRate || 1)
				.round(2)
				.toNumber()
		: bigMath.mul(
				bigMath.sub(
					subtotal,
					$getProductTotalRawPriceAddons(product),
					subtotalAddons.fixDiscount.amount,
					subtotalAddons.discount.amount,
				),
				newVatPercentage,
			);

	return {
		additionalFee: {
			isDivided: newAdditionalFeeIsDivided,
			type: newAdditionalFeeType,
			value: newAdditionalFeeValue,
			amount: newAdditionalFeeAmount,
			additionalFee: subtotalAddons.additionalFee.additionalFee,
		},
		fixDiscount: {
			isDivided: newFixDiscountIsDivided,
			type: newFixDiscountType,
			value: newFixDiscountValue,
			amount: newFixDiscountAmount,
			fixDiscount: subtotalAddons.fixDiscount.fixDiscount,
		},
		discount: {
			isDivided: newDiscountIsDivided,
			type: newDiscountType,
			value: newDiscountValue,
			amount: newDiscountAmount,
		},
		vat: {
			isIncluded: newVatIsIncluded,
			percentage: newVatPercentage,
			amount: newVatAmount,
		},
		adjustment: {
			amount: subtotalAddons.adjustment.amount,
		},
	};
};

const $getOptionSubtotalAddons = <
	TFileOption extends FileProductOptionInputLike = FileProductOptionInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
>({
	option,
	product,
	isCancelled,
}: {
	option: FileProductOptionInputLike<TFileOption>;
	product: FileProductInputLike<TFileProduct, TFileOption>;
	isCancelled: boolean;
}) => {
	if (isCancelled) return BASE_SUBTOTAL_ADDONS;

	const fileProduct = $getProductMetadata(product);
	const fileProductOption = $getOptionMetadata(option, fileProduct);

	const { subtotal: productSubtotal, subtotalAddons: productSubtotalAddons } =
		fileProduct;
	const {
		originalPriceAddons: optionOriginalPriceAddons,
		subtotal: optionSubtotal,
		subtotalAddons: optionSubtotalAddons,
	} = fileProductOption;

	const optionPriceRatioToProduct = bigMath.div(
		optionSubtotal,
		productSubtotal,
	);

	const newDiscountIsDivided = true;
	const newDiscountType = productSubtotalAddons.discount.type;
	const newDiscountValue = productSubtotalAddons.discount.value;
	const newDiscountAmount = bigMath.mul(
		optionPriceRatioToProduct,
		productSubtotalAddons.discount.amount,
	);

	const newAdditionalFeeIsDivided = true;
	const newAdditionalFeeType = productSubtotalAddons.additionalFee.type;
	const newAdditionalFeeValue = productSubtotalAddons.additionalFee.value;
	const newAdditionalFeeAmount = bigMath.mul(
		optionPriceRatioToProduct,
		productSubtotalAddons.additionalFee.amount,
	);

	const newFixDiscountIsDivided = true;
	const newFixDiscountType = productSubtotalAddons.fixDiscount.type;
	const newFixDiscountValue = productSubtotalAddons.fixDiscount.value;
	const newFixDiscountAmount = bigMath.mul(
		optionPriceRatioToProduct,
		productSubtotalAddons.fixDiscount.amount,
	);

	const newAdjustmentAmount = bigMath.mul(
		optionPriceRatioToProduct,
		productSubtotalAddons.adjustment.amount,
	);

	const newVatIsIncluded =
		!!optionOriginalPriceAddons.vat.percentage &&
		!!optionOriginalPriceAddons.vat.amount;
	const newVatPercentage = newVatIsIncluded
		? optionOriginalPriceAddons.vat.percentage
		: optionSubtotalAddons.vat.percentage;

	let [newVatAmount] = [0];
	const grossSales = bigMath.add(
		bigMath.sub(
			optionSubtotal,
			optionSubtotalAddons.fixDiscount.amount,
			optionSubtotalAddons.discount.amount,
		),
		optionSubtotalAddons.additionalFee.amount,
	);
	const addedVatScRate = bigMath.add(newVatPercentage);
	newVatAmount = newVatIsIncluded
		? new Big(
				new Big(
					new Big(grossSales).sub(
						new Big(grossSales).div(new Big(1).add(addedVatScRate)),
					),
				).mul(newVatPercentage),
			)
				.div(addedVatScRate || 1)
				.round(2)
				.toNumber()
		: bigMath.mul(
				bigMath.add(
					bigMath.sub(
						optionSubtotal,
						$getOptionTotalRawPriceAddons(fileProductOption, fileProduct),
						optionSubtotalAddons.fixDiscount.amount,
						optionSubtotalAddons.discount.amount,
					),
					newVatPercentage,
				),
				optionSubtotalAddons.additionalFee.amount,
			);

	return {
		additionalFee: {
			isDivided: newAdditionalFeeIsDivided,
			type: newAdditionalFeeType,
			value: newAdditionalFeeValue,
			amount: newAdditionalFeeAmount,
			additionalFee: productSubtotalAddons.additionalFee.additionalFee,
		},
		fixDiscount: {
			isDivided: newFixDiscountIsDivided,
			type: newFixDiscountType,
			value: newFixDiscountValue,
			amount: newFixDiscountAmount,
			fixDiscount: productSubtotalAddons.fixDiscount.fixDiscount,
		},
		discount: {
			isDivided: newDiscountIsDivided,
			type: newDiscountType,
			value: newDiscountValue,
			amount: newDiscountAmount,
		},
		vat: {
			isIncluded: newVatIsIncluded,
			percentage: newVatPercentage,
			amount: newVatAmount,
		},
		adjustment: {
			amount: newAdjustmentAmount,
		},
	};
};

const $getProductSubtotal = <
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>(
	product: FileProductInputLike<TFileProduct, TFileProductOption>,
) => {
	return new Big($getProductUnitPrice(product))
		.times(product.quantity || 0)
		.round(2)
		.toNumber();
};

const $getProductUnitPrice = <
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>(
	product: FileProductInputLike<TFileProduct, TFileProductOption>,
) => {
	const multiCurrency = product.multiCurrencyConfig;
	if (multiCurrency) {
		const price =
			(multiCurrency.priceConfigs ?? []).find(
				(c) => c.code === multiCurrency.selectedCurrency,
			)?.price ?? product.listedPrice;
		return new Big(price || 0)
			.add(
				product.options.reduce<BigSource>(
					(pre, cur) =>
						new Big(pre).add(
							new Big($getFileProductOptionUnitPrice(cur)).mul(
								cur.quantity || 1,
							),
						),
					0,
				),
			)
			.round(2)
			.toNumber();
	} else {
		return new Big(product.listedPrice || 0)
			.add(
				product.options.reduce<BigSource>(
					(pre, cur) =>
						new Big(pre).add(
							new Big($getFileProductOptionUnitPrice(cur)).mul(
								cur.quantity || 1,
							),
						),
					0,
				),
			)
			.round(2)
			.toNumber();
	}
};

const $getFileProductOptionUnitPrice = <
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>(
	option: FileProductOptionInputLike<TFileProductOption>,
) => {
	const multiCurrency = option.multiCurrencyConfig;
	if (multiCurrency) {
		const price = (multiCurrency.priceConfigs ?? []).find(
			(c) => c.code === multiCurrency.selectedCurrency,
		)?.price;
		return price ? price : option.listedPrice ? option.listedPrice : 0;
	}
	return option.listedPrice ? option.listedPrice : 0;
};

const $getProductTotalRawPriceAddons = <
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>(
	product: FileProductInputLike<TFileProduct, TFileProductOption>,
) => {
	return new Big(product.originalPriceAddons.vat.amount)
		.add(
			product.options.reduce<BigSource>(
				(pre, cur) => new Big(pre).add(cur.originalPriceAddons.vat.amount),
				0,
			),
		)
		.times(product.quantity)
		.round(2)
		.toNumber();
};

const $getOptionTotalRawPriceAddons = <
	TFileOption extends FileProductOptionInputLike = FileProductOptionInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
>(
	option: FileProductOptionInputLike<TFileOption>,
	product: FileProductInputLike<TFileProduct, TFileOption>,
) => {
	return new Big(option.originalPriceAddons.vat.amount)
		.times($getOptionServingQuantity(option, product))
		.round(2)
		.toNumber();
};

const $addFileToFileGroupCalculation = <
	TFile extends FileInputLike = FileInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>(
	group,
	file: FileInputLike<TFile, TFileProduct, TFileProductOption>,
) => {
	const {
		$file,
		$isAllProductsWithVat,
		$isSomeProductsWithVat,
		products,
		cancelledProducts,
		subtotal,
		additionalFeeAmount,
		fixDiscountAmount,
		discountAmount,
		netAmount,
		vatAmount,
		adjustmentAmount,
		grossAmount,
		paymentAmount,
		unpaidAmount,
		cancelledAmount,
	} = calculateFile(file);

	return {
		$files: group.$files.concat($file),
		$isAllProductsWithVat: group.$isAllProductsWithVat && $isAllProductsWithVat,
		$isSomeProductsWithVat:
			group.$isSomeProductsWithVat || $isSomeProductsWithVat,
		products: group.products.concat(products),
		cancelledProducts: group.products.concat(cancelledProducts),
		subtotal: bigMath.add(group.subtotal, subtotal),
		discountAmount: bigMath.add(group.discountAmount, discountAmount),
		additionalFeeAmount: bigMath.add(
			group.additionalFeeAmount,
			additionalFeeAmount,
		),
		fixDiscountAmount: bigMath.add(group.fixDiscountAmount, fixDiscountAmount),
		netAmount: bigMath.add(group.netAmount, netAmount),
		vatAmount: bigMath.add(group.vatAmount, vatAmount),
		adjustmentAmount: bigMath.add(group.adjustmentAmount, adjustmentAmount),
		grossAmount: bigMath.add(group.grossAmount, grossAmount),
		paymentAmount: bigMath.add(group.paymentAmount, paymentAmount),
		unpaidAmount: bigMath.add(group.unpaidAmount, unpaidAmount),
		cancelledAmount: bigMath.add(group.cancelledAmount, cancelledAmount),
	};
};

const $addFileToReportCalculation = <
	TFile extends FileInputLike = FileInputLike,
	TFileProduct extends FileProductInputLike = FileProductInputLike,
	TFileProductOption extends
		FileProductOptionInputLike = FileProductOptionInputLike,
>(
	group,
	file: FileInputLike<TFile, TFileProduct, TFileProductOption>,
) => {
	const {
		subtotal,
		additionalFeeAmount,
		fixDiscountAmount,
		discountAmount,
		netAmount,
		vatAmount,
		adjustmentAmount,
		grossAmount,
		paymentAmount,
		unpaidAmount,
		cancelledAmount,
	} = calculateFile(file);

	return {
		subtotal: bigMath.add(group.subtotal, subtotal),
		discountAmount: bigMath.add(group.discountAmount, discountAmount),
		additionalFeeAmount: bigMath.add(
			group.additionalFeeAmount,
			additionalFeeAmount,
		),
		fixDiscountAmount: bigMath.add(group.fixDiscountAmount, fixDiscountAmount),
		netAmount: bigMath.add(group.netAmount, netAmount),
		vatAmount: bigMath.add(group.vatAmount, vatAmount),
		adjustmentAmount: bigMath.add(group.adjustmentAmount, adjustmentAmount),
		grossAmount: bigMath.add(group.grossAmount, grossAmount),
		paymentAmount: bigMath.add(group.paymentAmount, paymentAmount),
		unpaidAmount: bigMath.add(group.unpaidAmount, unpaidAmount),
		cancelledAmount: bigMath.add(group.cancelledAmount, cancelledAmount),
	};
};

const baseReportValue = {
	subtotal: 0,
	discountAmount: 0,
	additionalFeeAmount: 0,
	fixDiscountAmount: 0,
	netAmount: 0,
	vatAmount: 0,
	adjustmentAmount: 0,
	grossAmount: 0,
	paymentAmount: 0,
	unpaidAmount: 0,
	cancelledAmount: 0,
};

const baseFileGroupValue = {
	$files: [] as FileInputLike[],
	$isAllProductsWithVat: true,
	$isSomeProductsWithVat: false,
	products: [] as FileInputLike["products"][],
	cancelledProducts: [] as FileInputLike["cancelledProducts"][],
	subtotal: 0,
	discountAmount: 0,
	additionalFeeAmount: 0,
	fixDiscountAmount: 0,
	netAmount: 0,
	vatAmount: 0,
	adjustmentAmount: 0,
	grossAmount: 0,
	paymentAmount: 0,
	unpaidAmount: 0,
	cancelledAmount: 0,
};
